// Small shared pieces for the booking screens (P2.4a): the copy helper (every
// string lives under brandAmbassadors:booking.*, en + sw), the action runner
// (one fresh Idempotency-Key per user action; every backend refusal — incl.
// 403 human_action_required — toasted as the backend says it), and form bits.
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Badge } from '@/features/shared/components/ui/badge';
import { getApiErrorMessage } from '@/lib/apiError';
import { errorCode, errorStatus, newActionKey } from '@/services/baBookingService';

export type K = (key: string, defaultValue: string, o?: Record<string, unknown>) => string;

/** booking.* copy: `k('confirm.title', 'Confirm booking')`. The en JSON carries
 *  exactly these defaults (bookingCopy.test.ts). */
export function useBk(): { k: K; lang: string } {
  const { t, i18n } = useTranslation('brandAmbassadors');
  const k: K = useCallback(
    (key, defaultValue, o = {}) => t(`booking.${key}`, { defaultValue, ...o }) as string,
    [t],
  );
  return { k, lang: i18n?.language || 'en' };
}

/** The human-only refusal (H): a person must do this, not an assistant. */
export const isHumanOnly = (e: unknown) => errorStatus(e) === 403 && errorCode(e) === 'human_action_required';

/**
 * Runs one user action. `fn` gets a FRESH Idempotency-Key (crypto.randomUUID)
 * for this click. Success toasts `ok`; failure toasts the backend's message via
 * getApiErrorMessage (403 human_action_required included, prefixed so it is
 * clear a person must act). Returns the result, or undefined on failure.
 */
export function useRunAction(k: K) {
  const [busy, setBusy] = useState(false);
  const run = useCallback(async <T,>(fn: (key: string) => Promise<T>, ok?: string,
    onError?: (e: unknown) => boolean | void): Promise<T | undefined> => {
    setBusy(true);
    try {
      const out = await fn(newActionKey());
      if (ok) toast.success(ok);
      return out;
    } catch (e) {
      if (onError && onError(e) === true) return undefined; // handled
      const say = (msg: string) => (isHumanOnly(e) ? k('errors.human_only', 'A person must do this: {{msg}}', { msg }) : msg);
      toast.error(say(getApiErrorMessage(e, k('errors.action', 'That didn’t work. Try again.'))));
      return undefined;
    } finally {
      setBusy(false);
    }
  }, [k]);
  return { busy, run };
}

export const Field: React.FC<{ label: string; htmlFor?: string; hint?: string; children: React.ReactNode; className?: string }> = ({
  label, htmlFor, hint, children, className,
}) => (
  <div className={`space-y-1 ${className ?? ''}`}>
    <label htmlFor={htmlFor} className="block text-sm font-medium text-foreground">{label}</label>
    {children}
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

export const selectClass =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

export const NativeSelect: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = ({ className, ...p }) => (
  <select {...p} className={`${selectClass} ${className ?? ''}`} />
);

const STATUS_VARIANT: Record<string, 'success' | 'warning' | 'danger' | 'info' | 'neutral'> = {
  draft: 'neutral', requested: 'info', quoting: 'info', awaiting_confirmation: 'warning', confirmed: 'success',
  cancelled: 'danger', declined: 'danger', expired: 'neutral', issued: 'info', accepted: 'success', rejected: 'danger',
  superseded: 'neutral', invited: 'info', withdrawn: 'neutral', open: 'neutral', filled: 'success',
  replacement_pending: 'warning', active: 'success', retired: 'neutral',
};

const STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', requested: 'Requested', quoting: 'Quoting', awaiting_confirmation: 'Awaiting confirmation',
  confirmed: 'Confirmed', cancelled: 'Cancelled', declined: 'Declined', expired: 'Expired', issued: 'Issued',
  accepted: 'Accepted', rejected: 'Rejected', superseded: 'Superseded', invited: 'Invited', withdrawn: 'Withdrawn',
  open: 'Open', filled: 'Filled', replacement_pending: 'Replacement pending', active: 'Active', retired: 'Retired',
};

export const StatusBadge: React.FC<{ status: string; k: K; label?: string }> = ({ status, k, label }) => (
  <Badge variant={STATUS_VARIANT[status] ?? 'neutral'} data-testid="status-badge">
    {label ?? k(`status.${status}`, STATUS_LABEL[status] ?? status.replace(/_/g, ' '))}
  </Badge>
);
